import type { BasePromotionSetting, Promotion } from '@empathyco/playboard-core-domain'
import type { AppConfig } from '../../models/config'
import type { PromotionFactory } from '../../modules/promotion'
import type { PromotionDomainService } from '../interfaces/domain/promotion-domain.service'
import { deepEqual } from '@empathyco/x-utils'
import { inject, injectable } from 'inversify'
import { TYPES } from '../../config/inversify.types'
import { FilterIdEnum } from '../../enums/filter-id.enum'

@injectable()
export class PlatformPromotionDomainService<
  PromotionSetting extends BasePromotionSetting = Promotion['setting'],
> implements PromotionDomainService<PromotionSetting>
{
  public constructor(
    @inject(TYPES.PromotionFactory) protected factory: PromotionFactory<PromotionSetting>,
    @inject(TYPES.AppConfig) protected appConfig: AppConfig,
  ) {}

  getEmptyInstance(): Promotion<PromotionSetting> {
    return this.factory()
  }

  getEmptySetting(): PromotionSetting {
    return this.factory().setting
  }

  isEmptySetting(promotion: Promotion<PromotionSetting>['setting']): boolean {
    return deepEqual(promotion, this.factory().setting)
  }

  isValid(item: Promotion<PromotionSetting>): boolean {
    return !!(
      this.isSettingValid(item.setting) &&
      this.hasLangPivotValues(item.criteria.pivots) &&
      this.isNameValid(item.name) &&
      (item.criteria.matchAll || item.criteria.queries.length)
    )
  }

  private isNameValid(name: Promotion['name']): boolean {
    return this.appConfig.useShoppablePromotions ? !!name : true
  }

  isSettingValid(setting: Promotion<PromotionSetting>['setting']): boolean {
    return !!(
      setting.type &&
      setting.position &&
      this.isUrlValid(setting.config?.url) &&
      this.isImageUrlValid(setting.imageUrl) &&
      this.isTitleValid(setting.title)
    )
  }

  isUrlValid(url?: string): boolean {
    return !!url
  }

  isImageUrlValid(imageUrl?: string): boolean {
    return !!imageUrl
  }

  isTitleValid(title: string): boolean {
    const titleMaxLength = this.getTitleMaxLength()
    return !!title && (titleMaxLength === undefined || title.length <= titleMaxLength)
  }

  getTitleMaxLength(): number | undefined {
    return undefined
  }

  private hasLangPivotValues(pivots: Promotion['criteria']['pivots']): boolean {
    const lang = pivots.find(pivot => pivot.name === FilterIdEnum.LANG)
    return !!lang && lang.values.length > 0
  }

  getTitle(promotion: Promotion<PromotionSetting>['setting']): string {
    return ''
  }

  getSubtitle(promotion: Promotion<PromotionSetting>['setting']): string {
    return ''
  }

  getButtonText(promotion: Promotion<PromotionSetting>['setting']): string {
    return ''
  }

  getButtonAriaText(promotion: Promotion<PromotionSetting>['setting']): string {
    return ''
  }
}
